import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { fetchServerData } from "../utils/fetchServerData";

import style from "../css/modalAdminPanel.module.css";

const SORT_FIELDS = [
  { label: "First Name", value: "first_name" },
  { label: "Last Name", value: "last_name" },
  { label: "Email", value: "email_address" },
  { label: "Created At", value: "created_at" },
  { label: "Last Sign-In", value: "last_sign_in_at" },
];

export function AdminPanel() {
  const [tab, setTab] = useState(0);
  const [invites, setInvites] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [statusMsg, setStatusMsg] = useState({ message: "", color: "error" });

  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [sortField, setSortField] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("-");
  const [page, setPage] = useState(1);
  const limit = 10;

  // fetch invites
  const loadInvites = async () => {
    setInviteLoading(true);
    try {
      const { invites: data } = await fetchServerData(
        "/admin/invites?page=1&limit=100"
      );
      setInvites(data);
    } catch {
      setStatusMsg({ message: "Failed to load invites", color: "error" });
    } finally {
      setInviteLoading(false);
    }
  };

  // fetch users
  const loadUsers = async () => {
    setUserLoading(true);
    try {
      const order_by = `${sortOrder}${sortField}`;
      const { users: u, totalCount: tc } = await fetchServerData(
        `/admin/users?order_by=${order_by}&page=${page}&limit=${limit}`
      );
      setUsers(u);
      setTotalCount(tc);
    } catch {
      setStatusMsg({ message: "Failed to load users", color: "error" });
    } finally {
      setUserLoading(false);
    }
  };

  //DEBUG TEST
  async function debugAdmin() {
    // fetchServerData attaches Authorization: Bearer <token>
    const data = await fetchServerData("/admin/_debug");
    console.log("ADMIN DEBUG:", data);
  }

  // initial fetch and refetch on params
  useEffect(() => {
    loadInvites();
  }, []);
  useEffect(() => {
    loadUsers();
  }, [sortField, sortOrder, page]);

  const sendInvite = async (e) => {
    e.preventDefault();
    setStatusMsg({ message: "", color: "error" });
    try {
      await fetchServerData("/admin/invites", {
        method: "POST",
        body: JSON.stringify({ email }),
      });
      setStatusMsg({ message: `Invitation sent to ${email}`, color: "green" });
      setEmail("");
      await loadInvites();
    } catch {
      setStatusMsg({ message: "Error sending invite.", color: "error" });
    }
  };

  const revokeInvite = async (id) => {
    try {
      await fetchServerData(`/admin/invites/${id}`, { method: "DELETE" });
      setStatusMsg({ message: "Invitation revoked", color: "green" });
      await loadInvites();
    } catch {
      setStatusMsg({ message: "Error revoking invite.", color: "error" });
    }
  };

  const formatDate = (iso) =>
    new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <Box sx={{ p: 3, overflowY: "auto", width: 800 }}>
      {statusMsg.message && (
        <Typography color={statusMsg.color} sx={{ mb: 2 }}>
          {statusMsg.message}
        </Typography>
      )}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        classes={{
          root: style.tabsRoot,
          indicator: style.tabsIndicator,
        }}
      >
        <Tab
          label="Invitations"
          classes={{
            root: style.tabRoot,
            selected: style.tabSelected,
            wrapper: style.tabWrapper,
          }}
        />
        <Tab
          label="Users"
          classes={{
            root: style.tabRoot,
            selected: style.tabSelected,
            wrapper: style.tabWrapper,
          }}
        />
      </Tabs>

      {tab === 0 && (
        <Box>
          {/* Invite Form */}
          <Box
            component="form"
            onSubmit={sendInvite}
            className={style.inviteForm}
          >
            <TextField
              size="small"
              fullWidth
              label="User Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Button type="submit" variant="contained" size="small">
              Send
            </Button>
          </Box>

          <button onClick={debugAdmin}>DEBUG TEST</button>

          {/* Pending Invites */}
          {inviteLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 240, overflowY: "auto" }}>
              {invites.length > 0 ? (
                invites.map((inv) => (
                  <ListItem
                    key={inv.id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={() => revokeInvite(inv.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={inv.emailAddress}
                      secondary={formatDate(inv.createdAt)}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary="No pending invites" />
                </ListItem>
              )}
            </List>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {/* Sorting Toolbar */}
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Sort by</InputLabel>
              <Select
                value={sortField}
                label="Sort by"
                onChange={(e) => setSortField(e.target.value)}
              >
                {SORT_FIELDS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <MenuItem value="+">Ascending</MenuItem>
                <MenuItem value="-">Descending</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          {userLoading ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer
                component={Paper}
                className={style.usersTable}
                sx={{ mb: 2 }}
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Date Created</TableCell>
                      <TableCell>Last Sign-In</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((u) => (
                      <TableRow key={u.id}>
                        <TableCell>
                          {u.emailAddresses[0]?.emailAddress}
                        </TableCell>
                        <TableCell>
                          {u.firstName} {u.lastName}
                        </TableCell>
                        <TableCell>{formatDate(u.createdAt)}</TableCell>
                        <TableCell>
                          {u.lastSignInAt ? formatDate(u.lastSignInAt) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Pagination
                  count={Math.ceil(totalCount / limit)}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  size="small"
                />
              </Box>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}
